import { VEC2, Vector, smoothstep } from 'math'
import { KNOB_SIZE, RAIL_HEIGHT } from '../constants'
import type { KnobLine } from '../types'
import type { Ref } from 'vue'
import { svgCurve } from '@/utils/svg'
import type { RenderLinesProps } from './types'
import { getLineRenderUID } from './utils'

const renderKnobLines_default = (lines: Ref<KnobLine[]>, _props: RenderLinesProps) => {
  return lines.value.map((line) => {
    const pa = line.knobA?.position || line.pa || VEC2(0, 0)
    const pb = line.knobB?.position || line.pb || VEC2(0, 0)
    const len = pb.sub(pa).mag()
    const ka = line.knobA
    const kb = line.knobB

    return (
      <div
        class="rounded-sm"
        style={{
          position: 'absolute',
          height: RAIL_HEIGHT / 2 + 'px',
          width: len + 'px',
          left: pa.x + 'px',
          top: 0,
          bottom: 0,
          pointerEvents: 'none',
          marginBottom: 'auto',
          marginTop: 'auto',
          background:
            ka?.color && kb?.color
              ? `linear-gradient(to right, ${ka?.color}, ${kb?.color})`
            : ka.color ? `linear-gradient(to left, transparent, ${ka?.color})` : kb?.color ? `linear-gradient(to right, transparent, ${kb?.color})`  :  (ka?.color || kb?.color || `red`)
        }}
      />
    )
  })
}

const renderKnobLines_rope = (lines: Ref<KnobLine[]>, props: RenderLinesProps) => {
  const uid = getLineRenderUID(props)
  const railWidth = props.range.appearance?.knobRailWidth || RAIL_HEIGHT / 2

  const mul = 32
  const h = RAIL_HEIGHT * mul

  return (
    <div class="w-full absolute left-0 top-0">
      {lines.value.map((line, lineIndex) => {
        const pa = line.knobA?.position || line.pa || VEC2(0, 0)
        const pb = line.knobB?.position || line.pb || VEC2(0, 0)
        const len = pb.sub(pa).mag()
        const ka = line.knobA
        const kb = line.knobB

        const Y = pa.y + RAIL_HEIGHT * (mul / 2)
        const lineUID = `${uid}-${ka?.uid}-${kb?.uid}-${lineIndex}`
        return (
          <svg
            width="100%"
            height={RAIL_HEIGHT * mul + 'px'}
            xmlns="http://www.w3.org/2000/svg"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              marginTop: 'auto',
              marginBottom: 'auto',
              pointerEvents: 'none'
            }}
          >
            <defs>
              <linearGradient id={`gradient-${lineUID}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stop-color={ka?.color || 'transparent'} />
                <stop offset="100%" stop-color={kb?.color || 'transparent'} />
              </linearGradient>
            </defs>
            {svgCurve({
              points: () => {
                const start = VEC2(pa.x + Number(lineIndex > 0) * (KNOB_SIZE * 0.5), Y)
                const end = VEC2(
                  pb.x + Number(lineIndex < lines.value.length - 1) * KNOB_SIZE * 0.5,
                  Y
                )
                const mid = (start.x + end.x) * 0.5

                const dist = start.distance(end)
                const invDist = smoothstep(len + 2 * KNOB_SIZE, 0.0, dist)

                //const kx = 1 * KNOB_SIZE * invDist
                const ky = h * 0.25 * invDist

                const c1 = VEC2(mid, start.y + ky)
                const c2 = VEC2(mid, end.y + ky)

                return [
                  {
                    start,
                    end,
                    control: [c1, c2]
                  }
                ]
              },
              fill: 'transparent',
              ...(len <= 0.00001
                ? {
                    stroke:
                      ka?.color && kb?.color
                        ? Vector.fromColor(ka.color)
                            .add(Vector.fromColor(kb.color))
                            .scale(0.5)
                            .toRGB(4)
                        : 'black'
                  }
                : {
                    stroke: `url(#gradient-${lineUID})`
                  }),
              'stroke-width': railWidth + 'px'
              //stroke: ka.color || 'red',
            })}
          </svg>
        )
      })}
    </div>
  )
}

export const renderKnobLines = (lines: Ref<KnobLine[]>, props: RenderLinesProps) => {
  switch (props.range.behaviour) {
    case 'rope':
      return renderKnobLines_rope(lines, props)
    default:
      return renderKnobLines_default(lines, props)
  }
}
